import {
  ApolloClient,
  ApolloLink,
  ApolloProvider as BaseApolloProvider,
  createHttpLink,
  InMemoryCache,
  Operation,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { ReactNode, useContext } from 'react'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import { CurrentTenantContext } from './current_tenant_context'
import { createConsumer } from '@rails/actioncable'
import ActionCableLink from 'graphql-ruby-client/subscriptions/ActionCableLink'
import { getMainDefinition } from '@apollo/client/utilities'

interface Props {
  children: ReactNode
}

export const ApolloProvider = ({ children }: Props) => {
  const { currentCompany } = useContext(CurrentTenantContext)

  const httpLink = createHttpLink({
    uri: import.meta.env.VITE_RIPPLE_GRAPHQL_ENDPOINT,
  })

  const hasSubscriptionOperation = ({ query }: Operation) => {
    const definition = getMainDefinition(query)
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
  }

  const { token } = useContext<IAuthContext>(AuthContext)

  const cable = createConsumer(`${import.meta.env.VITE_RIPPLE_CABLE_URL}?access_token=${token}`)

  const authLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : ''
      },
    }
  })

  const link = ApolloLink.split(
    hasSubscriptionOperation,
    new ActionCableLink({ cable }),
    httpLink
  )

  const client = new ApolloClient({
    link: authLink.concat(link),
    cache: new InMemoryCache,
    defaultOptions: {
      query: { fetchPolicy: 'cache-first', variables: { companyId: currentCompany?.id || '' } },
      watchQuery: { fetchPolicy: 'cache-and-network', variables: { companyId: currentCompany?.id } },
      mutate: { variables: { companyId: currentCompany?.id } },
    }
  })

  return (
    <BaseApolloProvider client={client}>
      { children }
    </BaseApolloProvider>
  )
}
